import FailedIcon from "@mui/icons-material/ClearRounded";
import SuccessIcon from "@mui/icons-material/Done";
import { Button, Tab, Tabs } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog, selectOptionSchema } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps,
  SelectOption
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { LinearProgressWithLabelBig } from "components/common/LinearProgressWithLabelBig";
import {
  TABLE_SORTING_TYPES,
  TableColumn
} from "components/common/Table/types";
import { TableCollapsible } from "components/common/TableCollapsible";
import { CollapsibleData } from "components/common/TableCollapsible/types";
import { format, startOfMonth, sub } from "date-fns";
import dayjs, { Dayjs } from "dayjs";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { userSelector } from "modules/auth/selectors";
import { billingActions } from "modules/billing/slices";
import {
  isProjectBillingBackupsLicensesDataLoadingSelector,
  isProjectBillingBackupsStorageDataLoadingSelector,
  isProjectBillingDatabasesDataLoadingSelector,
  isProjectBillingInstancesDataLoadingSelector,
  isProjectBillingS3DataLoadingSelector,
  isProjectBillingVolumesDataLoadingSelector,
  organizationBillingDataSelector,
  projectBillingDataSelector,
  projectBillingInstancesDataSelector,
  tableProjectBillingBackupsLicensesRecordsSelector,
  tableProjectBillingBackupsLicensesRecordsTotalSelector,
  tableProjectBillingBackupsStorageRecordsSelector,
  tableProjectBillingBackupsStorageRecordsTotalSelector,
  tableProjectBillingDatabasesRecordsSelector,
  tableProjectBillingDatabasesRecordsTotalSelector,
  tableProjectBillingInstancesRecordsSelector,
  tableProjectBillingInstancesRecordsTotalSelector,
  tableProjectBillingS3RecordsSelector,
  tableProjectBillingS3RecordsTotalSelector,
  tableProjectBillingSummaryRecordsSelector,
  tableProjectBillingSummaryRecordsTotalSelector,
  tableProjectBillingVolumesRecordsSelector,
  tableProjectBillingVolumesRecordsTotalSelector,
  isProjectBudgetLimitUpdatingSelector
} from "modules/billing/selectors";
import {
  BILLING_ROLES,
  TableBillingBackupsLicensesRecord,
  TableBillingBackupsStorageRecord,
  TableBillingDatabasesRecord,
  TableBillingInstancesFlavor,
  TableBillingInstancesRecord,
  TableBillingS3Record,
  TableBillingSummaryRecord,
  TableBillingVolumesRecord
} from "modules/billing/types";
import { databasesActions } from "modules/databases/slices";
import { enterprisesActions } from "modules/enterprises/slices";
import { instancesActions } from "modules/instances/slices";
import { networksActions } from "modules/networks/slices";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import { storageActions as volumesActions } from "modules/storage/slices";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { downloadFile } from "utils/downloadFile";
import { getParentPath } from "utils/getParentPath";
import { array, number } from "yup";
import { appConfig } from "../../appConfig";
import { API_PATH } from "../../axios";
import { DATE_FORMATS, DEFAULT_CURRENCY, ROUTES } from "../../constants";
import * as s from "./styles";
import { DATE_TYPES, DIALOG_TYPES, TABS } from "./types";
import { Loader } from "components/common/Loader";

const CURRENCY_SELECT_ID = "currency";

// the same for a table and its nested one, so the cost columns line up
const COST_COLUMN_WIDTH = "150px";

const TAB_TITLES: { [key in TABS]: string } = {
  [TABS.SUMMARY]: "Billing Summary",
  [TABS.INSTANCES]: "Instances",
  [TABS.VOLUMES]: "Volumes",
  // [TABS.DATABASES]: "Databases",
  [TABS.OBJECT_STORAGE]: "Object Storage"
};

const POLL_ID_PREFIX = "PROJECT_BILLING";

const POLL_IDS = {
  project_billing: "PROJECT_BILLING",
  organization_billing: "ORGANIZATION_BILLING"
};

export const BillingProject: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    serviceName: string;
    organizationId: string;
    regionId: string;
    projectId: string;
  }>();

  const history = useNavigate();
  const user = useSelector(userSelector);
  const billingOrganization = useSelector(organizationBillingDataSelector);
  const billingProject = useSelector(projectBillingDataSelector);
  const isProjectBudgetLimitUpdating = useSelector(
    isProjectBudgetLimitUpdatingSelector
  );
  const isOperationInProgress = isProjectBudgetLimitUpdating;
  const previousIsOperationInProgress = usePrevious(isOperationInProgress);

  const budgetThreshold = billingProject?.budget?.limits;

  const summaryTableBillingData = useSelector(
    tableProjectBillingSummaryRecordsSelector
  );

  const summaryTableBillingDataTotal = useSelector(
    tableProjectBillingSummaryRecordsTotalSelector
  );

  const showBackups =
    appConfig.isBackupsEnabled ||
    user?.attributes?.["canaryFeatures"]?.includes("backups");
  const showDatabases = appConfig.isDbaasEnabled;

  const hiddenIds = useMemo(() => {
    const ids: string[] = [];
    if (!showBackups) ids.push("Backups");
    if (!showDatabases) ids.push("Databases");
    return ids;
  }, [showBackups, showDatabases]);

  const summaryTableBillingDataFiltered = useMemo(() => {
    const data = summaryTableBillingData || [];
    if (hiddenIds.length === 0) return data;
    return data.filter((row) => !hiddenIds.includes(row.id));
  }, [summaryTableBillingData, hiddenIds]);

  const instancesBillingData = useSelector(projectBillingInstancesDataSelector);
  const instancesTableBillingData = useSelector(
    tableProjectBillingInstancesRecordsSelector
  );
  const instancesTableBillingDataTotal = useSelector(
    tableProjectBillingInstancesRecordsTotalSelector
  );

  const volumesTableBillingData = useSelector(
    tableProjectBillingVolumesRecordsSelector
  );
  const volumesTableBillingDataTotal = useSelector(
    tableProjectBillingVolumesRecordsTotalSelector
  );

  const databasesTableBillingData = useSelector(
    tableProjectBillingDatabasesRecordsSelector
  );
  const databasesTableBillingDataTotal = useSelector(
    tableProjectBillingDatabasesRecordsTotalSelector
  );

  const s3TableBillingData = useSelector(tableProjectBillingS3RecordsSelector);
  const s3TableBillingDataTotal = useSelector(
    tableProjectBillingS3RecordsTotalSelector
  );

  const backupsStorageTableBillingData = useSelector(
    tableProjectBillingBackupsStorageRecordsSelector
  );
  const backupsStorageTableBillingDataTotal = useSelector(
    tableProjectBillingBackupsStorageRecordsTotalSelector
  );

  const backupsLicensesTableBillingData = useSelector(
    tableProjectBillingBackupsLicensesRecordsSelector
  );
  const backupsLicensesTableBillingDataTotal = useSelector(
    tableProjectBillingBackupsLicensesRecordsTotalSelector
  );

  const isProjectBillingInstancesDataLoading = useSelector(
    isProjectBillingInstancesDataLoadingSelector
  );
  const isProjectBillingVolumesDataLoading = useSelector(
    isProjectBillingVolumesDataLoadingSelector
  );
  const isProjectBillingDatabasesDataLoading = useSelector(
    isProjectBillingDatabasesDataLoadingSelector
  );
  const isProjectBillingS3DataLoading = useSelector(
    isProjectBillingS3DataLoadingSelector
  );
  const isProjectBillingBackupsStorageDataLoading = useSelector(
    isProjectBillingBackupsStorageDataLoadingSelector
  );
  const isProjectBillingBackupsLicensesDataLoading = useSelector(
    isProjectBillingBackupsLicensesDataLoadingSelector
  );

  const currentDate = useMemo(() => new Date(), []);
  const [minDate] = useState<Dayjs>(() =>
    dayjs(sub(currentDate, { days: 120 })).startOf("day")
  );

  const [startDate, setStartDate] = useState<Dayjs | null>(() =>
    dayjs(startOfMonth(currentDate)).startOf("day")
  );

  const [endDate, setEndDate] = useState<Dayjs | null>(() =>
    dayjs(currentDate).startOf("day")
  );

  const startDateStr = startDate?.format(DATE_FORMATS.DAYJS_ISO_DATE) ?? null;
  const endDateStr = endDate?.format(DATE_FORMATS.DAYJS_ISO_DATE) ?? null;

  const handleDateChange = useCallback(
    (dateType: DATE_TYPES, value: Dayjs | null) => {
      switch (dateType) {
        case DATE_TYPES.START:
          setStartDate(value);
          break;
        case DATE_TYPES.END:
          setEndDate(value);
          break;
      }
    },
    []
  );

  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);

  const tableCurrencies = useMemo(
    () =>
      instancesBillingData
        ? Object.keys(instancesBillingData.costs)
        : ["chf", "eur"],
    [instancesBillingData]
  );

  const handleCurrencyChange = useCallback((event: SelectChangeEvent) => {
    setCurrency(event.target.value);
  }, []);

  const getStoppedGPUFlavorTip = useCallback(
    (fl: TableBillingInstancesFlavor, currency: string) => {
      const cur = currency.toLowerCase();
      const hrs = fl?.stopped_time_hours;
      const costs = fl?.stopped_time_cost;
      if (!hrs || !costs || costs[cur] === null || costs[cur] === undefined)
        return "";

      const val = costs[cur];
      return `Includes GPU reservation costs: ${Number(val).toFixed(2)} ${cur.toUpperCase()} (${hrs} hrs)`;
    },
    []
  );

  const tableSummaryColumns: TableColumn<TableBillingSummaryRecord>[] = [
    { key: "id", label: "Resource Type", width: "70%" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      sortingType: TABLE_SORTING_TYPES.NUMBER
    }
  ];

  const tableVolumesColumns: TableColumn<TableBillingVolumesRecord>[] = [
    { key: "id", label: "Volume ID" },
    { key: "name", label: "Volume Name" },
    { key: "status", label: "Status", width: "15%" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH,
      sortingType: TABLE_SORTING_TYPES.NUMBER
    }
  ];
  const collapsibleVolumesColumns: TableColumn<CollapsibleData>[] = [
    { key: "description", label: "Size" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH
    }
  ];

  const tableInstancesColumns: TableColumn<TableBillingInstancesRecord>[] = [
    { key: "id", label: "Instance ID", width: "350px" },
    { key: "name", label: "Instance Name" },
    { key: "status", label: "Status", width: "15%" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH,
      sortingType: TABLE_SORTING_TYPES.NUMBER
    }
  ];
  const collapsibleInstanceColumns: TableColumn<CollapsibleData>[] = [
    { key: "name", label: "Flavor Name", width: "350px" },
    { key: "description", label: "Description" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH
    }
  ];

  const tableDatabasesColumns: TableColumn<TableBillingVolumesRecord>[] = [
    { key: "id", label: "Database ID" },
    { key: "name", label: "Database Name" },
    { key: "type", label: "Type" },
    { key: "status", label: "Status", width: "15%" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH,
      sortingType: TABLE_SORTING_TYPES.NUMBER
    }
  ];
  const collapsibleDatabasesColumns: TableColumn<CollapsibleData>[] = [
    { key: "name", label: "Description" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH
    }
  ];

  const tableS3Columns: TableColumn<TableBillingS3Record>[] = [
    { key: "id", label: "ID" },
    { key: "name", label: "Name", width: "25%" },
    // { key: "type", label: "Type" },
    { key: "status", label: "Status", width: "15%" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH,
      sortingType: TABLE_SORTING_TYPES.NUMBER
    }
  ];
  // const collapsibleS3Columns: TableColumn<CollapsibleData>[] = [
  //   { key: "name", label: "S3 Object Name" },
  //   {
  //     key: `cost-${currency}`,
  //     label: "Cost"
  //   }
  // ];

  const tableBackupsStorageColumns: TableColumn<TableBillingBackupsStorageRecord>[] =
    [
      { key: "id", label: "Workload ID" },
      { key: "name", label: "Workload Name" },
      // { key: "status", label: "Status", width: "15%" },
      {
        key: `cost-${currency}`,
        label: "Cost",
        width: COST_COLUMN_WIDTH,
        sortingType: TABLE_SORTING_TYPES.NUMBER
      }
    ];
  const collapsibleBackupsStorageColumns: TableColumn<CollapsibleData>[] = [
    { key: "id", label: "Backup ID" },
    { key: "name", label: "Backup Name" },
    // { key: "description", label: "Description" },
    { key: "size", label: "Backup Size" },
    { key: "status", label: "Status", width: "10%" },
    { key: "created_at", label: "Created" },
    {
      key: `cost-${currency}`,
      label: "Cost",
      width: COST_COLUMN_WIDTH
    }
  ];

  const tableBackupsLicensesColumns: TableColumn<TableBillingBackupsLicensesRecord>[] =
    [
      { key: "id", label: "Instance ID" },
      { key: "name", label: "Instance Name" },
      // { key: "status", label: "Status", width: "15%" },
      {
        key: `cost-${currency}`,
        label: "Cost",
        width: COST_COLUMN_WIDTH,
        sortingType: TABLE_SORTING_TYPES.NUMBER
      }
    ];
  const collapsibleBackupsLicensesColumns: TableColumn<CollapsibleData>[] = [
    { key: "id", label: "Backup ID" },
    { key: "name", label: "Backup Name" },
    // { key: "size", label: "Backup Size" },
    { key: "status", label: "Status", width: "10%" },
    { key: "description", label: "Workload ID" },
    { key: "created_at", label: "Created" }
  ];

  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.GENERATE_BILLING_REPORT, isOpened: false });

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
  }, [dialog]);

  const activeTabIndexFromParam = Object.keys(TAB_TITLES).find(
    (key) =>
      TAB_TITLES[key] &&
      String(TAB_TITLES[key]).toLowerCase() ===
        new URLSearchParams(location.search).get("tab")
  );
  const [activeTabIndex, setActiveTabIndex] = useState<TABS>(
    Number(activeTabIndexFromParam || TABS.SUMMARY)
  );
  const handleChangeTab = useCallback((e, value: number) => {
    setActiveTabIndex(value);
  }, []);

  const breadcrumbs: Breadcrumb[] = [
    {
      text: "Billing",
      url: generatePath(ROUTES.BILLING_ORGANIZATIONS)
    },
    {
      text: billingOrganization?.name || "",
      url: generatePath(ROUTES.BILLING_ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.BILLING_ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: billingProject?.name || "",
      url: generatePath(ROUTES.BILLING_PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    }
  ];

  const handleGenerateBillingReportButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.GENERATE_BILLING_REPORT,
      isOpened: true
    });
  }, []);

  const handleSetBudgetThresholdButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.SET_BUDGET_THRESHOLD,
      isOpened: true
    });
  }, []);

  useMount(() => {
    dispatch(
      billingActions.getProjectBillingDataRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!,
        orgId: matchParams.organizationId!
      })
    );
    if (startDateStr && endDateStr) {
      dispatch(
        createPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.organization_billing}`,
          actionCreator: billingActions.getOrganizationBillingDataRequest,
          params: {
            startDate: startDateStr,
            endDate: endDateStr,
            id: matchParams.organizationId!
          }
        })
      );
      // dispatch(
      //   billingActions.getOrganizationBillingDataRequest({
      //     startDate: startDateStr,
      //     endDate: endDateStr,
      //     id: matchParams.organizationId!
      //   })
      // );
    }
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(billingActions.clear());
    dispatch(projectsActions.clear());
    dispatch(instancesActions.clear());
    dispatch(volumesActions.clear());
    dispatch(networksActions.clear());
    if (appConfig.isDbaasEnabled) {
      dispatch(databasesActions.clear());
    }
  });

  useEffect(() => {
    if (
      // prevOrganization !== undefined &&
      // organization &&
      // prevBillingOrganization !== undefined && billingOrganization &&
      // [
      //   BILLING_ROLES.OWNER,
      //   BILLING_ROLES.ADMIN,
      //   BILLING_ROLES.BILLING_ADMIN
      // ].includes(billingOrganization?.role) &&
      startDateStr &&
      endDateStr
    ) {
      dispatch(
        billingActions.getProjectBillingInstancesDataRequest({
          startDate: startDateStr,
          endDate: endDateStr,
          regionId: matchParams.regionId!,
          organizationId: matchParams.organizationId!,
          projectId: matchParams.projectId!
        })
      );
      dispatch(
        billingActions.getProjectBillingVolumesDataRequest({
          startDate: startDateStr,
          endDate: endDateStr,
          regionId: matchParams.regionId!,
          organizationId: matchParams.organizationId!,
          projectId: matchParams.projectId!
        })
      );
      dispatch(
        billingActions.getProjectBillingS3DataRequest({
          startDate: startDateStr,
          endDate: endDateStr,
          regionId: matchParams.regionId!,
          organizationId: matchParams.organizationId!,
          projectId: matchParams.projectId!
        })
      );
      if (showDatabases) {
        dispatch(
          billingActions.getProjectBillingDatabasesDataRequest({
            startDate: startDateStr,
            endDate: endDateStr,
            regionId: matchParams.regionId!,
            organizationId: matchParams.organizationId!,
            projectId: matchParams.projectId!
          })
        );
      }
    }
  }, [
    dispatch,
    startDateStr,
    endDateStr,
    matchParams.organizationId,
    matchParams.regionId,
    matchParams.projectId,
    showDatabases
    // billingOrganization,
    // prevBillingOrganization
  ]);

  useEffect(() => {
    if (
      // prevOrganization !== undefined &&
      // organization &&
      // [ROLES.OWNER, ROLES.ADMIN].includes(organization.role) &&
      // prevBillingOrganization !== undefined && billingOrganization &&
      // [
      //   BILLING_ROLES.OWNER,
      //   BILLING_ROLES.ADMIN,
      //   BILLING_ROLES.BILLING_ADMIN
      // ].includes(billingOrganization?.role) &&
      startDateStr &&
      endDateStr
    ) {
      if (showBackups) {
        dispatch(
          billingActions.getProjectBillingBackupsStorageDataRequest({
            startDate: startDateStr,
            endDate: endDateStr,
            regionId: matchParams.regionId!,
            organizationId: matchParams.organizationId!,
            projectId: matchParams.projectId!
          })
        );
        dispatch(
          billingActions.getProjectBillingBackupsLicensesDataRequest({
            startDate: startDateStr,
            endDate: endDateStr,
            regionId: matchParams.regionId!,
            organizationId: matchParams.organizationId!,
            projectId: matchParams.projectId!
          })
        );
      }
    }
  }, [
    dispatch,
    startDateStr,
    endDateStr,
    matchParams.organizationId,
    matchParams.regionId,
    matchParams.projectId,
    showBackups
    // billingOrganization,
    // prevBillingOrganization
  ]);

  useEffect(() => {
    if (previousIsOperationInProgress && !isOperationInProgress) {
      if (!billingProject) {
        history(getParentPath(location.pathname, 4));
      } else {
        dispatch(
          billingActions.getProjectBillingDataRequest({
            regionId: matchParams.regionId!,
            id: matchParams.projectId!,
            orgId: matchParams.organizationId!
          })
        );
      }
    }
  }, [
    billingProject,
    history,
    previousIsOperationInProgress,
    isOperationInProgress,
    dispatch,
    matchParams.regionId,
    matchParams.projectId,
    matchParams.organizationId
  ]);

  const handleConfirmDownloadButtonClick = useCallback(
    (data: { start_date: string; end_date: string }) => {
      const startDate = format(
        new Date(data.start_date),
        DATE_FORMATS.ISO_DATE
      );
      const endDate = format(new Date(data.end_date), DATE_FORMATS.ISO_DATE);
      downloadFile(
        `${API_PATH}gotham-wayne-tower/method/billing/organizations/${matchParams.organizationId!}/regions/${matchParams.regionId!}/projects/${matchParams.projectId!}/download?start=${startDate}&end=${endDate}`
      );
      dispatch(
        notificationsActions.showNotification({
          title: "Billing report is generating. Download will start soon.",
          type: NOTIFICATION_TYPES.INFO
        })
      );
      handleCloseDialog();
    },
    [
      matchParams.organizationId,
      matchParams.regionId,
      matchParams.projectId,
      dispatch,
      handleCloseDialog
    ]
  );

  const handleConfirmSetBudgetThreshold = useCallback(
    (data: {
      value: number;
      currency: string;
      subscribers: SelectOption[];
    }) => {
      dispatch(
        billingActions.updateProjectBudgetLimitRequest({
          organizationId: matchParams.organizationId!,
          regionId: matchParams.regionId!,
          id: matchParams.projectId!,
          data: {
            value: data.value,
            currency: currency,
            subscribers: data.subscribers?.map((person) => person.value)
          }
        })
      );
      handleCloseDialog();
    },
    [
      currency,
      dispatch,
      handleCloseDialog,
      matchParams.organizationId,
      matchParams.projectId,
      matchParams.regionId
    ]
  );

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.GENERATE_BILLING_REPORT]: {
      title: "Generate billing report",
      // onConfirm: handleConfirmGenerateButtonClick,
      // confirmButtonLabel: "Generate",
      onConfirm: handleConfirmDownloadButtonClick,
      confirmButtonLabel: "Download",
      fields: [
        {
          name: "start_date",
          type: FIELD_TYPES.DATE_PICKER,
          label: "Start-date",
          minDate: minDate,
          disableFuture: true,
          defaultValue: startDate
        },
        {
          name: "end_date",
          type: FIELD_TYPES.DATE_PICKER,
          label: "End-date",
          disableFuture: true,
          defaultValue: endDate
        },
        {
          name: "notes",
          type: FIELD_TYPES.LABEL,
          label: `ℹ️ Billing data is available for previous 120 days`
        }
        // {
        //   name: "tab",
        //   type: FIELD_TYPES.LABEL,
        //   label: `\n`
        // },
        // {
        //   name: "info",
        //   type: FIELD_TYPES.LABEL,
        //   label: `The report will be sent to your account email within a few minutes.`
        // }
        // {
        //   name: "info2",
        //   type: FIELD_TYPES.LABEL,
        //   label: `If it's delayed, please contact our support team.`
        // }
      ]
    },
    [DIALOG_TYPES.SET_BUDGET_THRESHOLD]: {
      onConfirm: handleConfirmSetBudgetThreshold,
      title: "Set Budget Threshold",
      confirmButtonLabel: "Set",
      fields: [
        {
          name: "value",
          type: FIELD_TYPES.NUMBER,
          defaultValue: budgetThreshold
            ? currency === "eur"
              ? (budgetThreshold.eur ?? 0)
              : (budgetThreshold.chf ?? 0)
            : 0,
          min: 0,
          step: 10,
          label: "Budget Limit Amount",
          suffix: currency.toUpperCase(),
          helperText:
            "This threshold is used to monitor your monthly spending. Use 0 to disable the limit.",
          rules: number().min(0, "Please ensure the value is 0 or greater.")
        },
        {
          name: "subscribers",
          type: FIELD_TYPES.MULTISELECT,
          variant: "outlined",
          defaultValue:
            billingProject &&
            billingProject.budget &&
            billingProject?.budget?.subscribers.length > 0
              ? billingProject.budget.subscribers.map((email) => ({
                  label: email,
                  value: email
                }))
              : billingOrganization?.owner
                ? [
                    {
                      label: billingOrganization.owner,
                      value: billingOrganization.owner
                    }
                  ]
                : [],
          helperText: "Add administrators who should receive notifications.",
          label: "Subscribers",
          options: billingOrganization
            ? [
                {
                  label: `${billingOrganization.owner} (Owner)`,
                  value: billingOrganization.owner
                },
                ...billingOrganization.billing_admins.map((email) => ({
                  label: email,
                  value: email
                }))
              ]
            : [],

          rules: array(selectOptionSchema)
        },
        {
          name: "inUseWarning",
          type: FIELD_TYPES.LABEL,
          label: `\nℹ️ Subscribers will receive a daily email notification when 80% of budget is reached.`
        }
      ]
    }
  };

  const title = "Billing";

  const toolbarItems = (
    <>
      <span>
        <Button
          onClick={handleGenerateBillingReportButtonClick}
          disabled={!billingProject}
          variant="contained"
          sx={{ marginRight: "10px" }}
        >
          Generate billing report
        </Button>
      </span>
      <span>
        <Button
          onClick={handleSetBudgetThresholdButtonClick}
          disabled={!billingProject || !billingOrganization}
          variant="contained"
          sx={{ marginRight: "10px" }}
        >
          Set Budget Threshold
        </Button>
      </span>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          name={"start"}
          label={"Start date"}
          onChange={(value) =>
            handleDateChange(DATE_TYPES.START, value as Dayjs | null)
          }
          value={startDate || null}
          format={"DD MMM YYYY"}
          minDate={minDate}
          maxDate={endDate || undefined}
          disableFuture={true}
          sx={{ marginRight: "10px", maxWidth: "170px" }}
          slotProps={{ textField: { size: "small" } }}
        />
        <DatePicker
          name={"end"}
          label={"End date"}
          onChange={(value) =>
            handleDateChange(DATE_TYPES.END, value as Dayjs | null)
          }
          value={endDate || null}
          format={"DD MMM YYYY"}
          minDate={startDate || minDate}
          disableFuture={true}
          sx={{ marginRight: "10px", maxWidth: "170px", fontSize: "4px" }}
          slotProps={{ textField: { size: "small" } }}
        />
      </LocalizationProvider>
      {tableCurrencies.length > 0 && (
        <s.CurrencyFormControl>
          <InputLabel id={CURRENCY_SELECT_ID}>Currency</InputLabel>
          <Select
            label={"Currency"}
            labelId={CURRENCY_SELECT_ID}
            value={currency}
            onChange={handleCurrencyChange}
            sx={{ marginRight: "10px", maxWidth: "100px" }}
            size={"small"}
          >
            {tableCurrencies.map((currency) => (
              <MenuItem key={currency} value={currency}>
                {currency.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </s.CurrencyFormControl>
      )}
    </>
  );

  const tabConfig = [
    {
      title: "Billing Summary",
      enabled: true,
      content: (
        <TableCollapsible<TableBillingSummaryRecord>
          key={"summaryBilling"}
          isSearchEnabled={true}
          rows={summaryTableBillingDataFiltered || []}
          // collapsibleDataKey="items"
          // collapsibleDataColumns={[
          //   ...collapsibleSummaryColumns,
          // ]}
          columns={[...tableSummaryColumns]}
          totalRow={summaryTableBillingDataTotal || undefined}
          isLoading={
            !billingProject ||
            isProjectBillingInstancesDataLoading ||
            isProjectBillingVolumesDataLoading ||
            isProjectBillingDatabasesDataLoading ||
            isProjectBillingS3DataLoading ||
            isProjectBillingBackupsStorageDataLoading ||
            isProjectBillingBackupsLicensesDataLoading
          }
          toolbarItems={toolbarItems}
        />
      )
    },
    {
      title: "Instances",
      enabled: true,
      content: (
        <TableCollapsible<TableBillingInstancesRecord>
          key={"InstancesBilling"}
          rows={instancesTableBillingData || []}
          collapsibleDataKey="itemFlavors"
          isLoading={isProjectBillingInstancesDataLoading}
          isSearchEnabled={true}
          totalRow={instancesTableBillingDataTotal || undefined}
          toolbarItems={toolbarItems}
          columns={[...tableInstancesColumns]}
          collapsibleDataColumns={[...collapsibleInstanceColumns]}
          itemWithIcon={{
            withText: false,
            column: "status",
            getIcon: (value) => {
              if (value === "available")
                return <SuccessIcon color="success" fontSize="small" />;
              if (value === "deleted")
                return <FailedIcon color="error" fontSize="small" />;
              return <></>; // or default icon
            },
            tooltipText: (row) => {
              if (row.status === "deleted")
                return `deleted at ${String(row.deleted)}`;
              if (row.status === "available") return "available instance";
              return "";
            }
          }}
          collapsibleItemWithIcon={{
            withText: false,
            column: `cost-${currency}`,
            getIcon: (row, columnKey) => {
              const tip = getStoppedGPUFlavorTip(
                row as TableBillingInstancesFlavor,
                String(currency)
              );
              return tip ? (
                <s.CustomTableCellText
                  style={{
                    display: "inline-flex",
                    alignItems: "flex-start",
                    gap: 4
                  }}
                >
                  {row[columnKey]}
                  <span style={{ fontSize: 12, lineHeight: 1 }}>*</span>
                </s.CustomTableCellText>
              ) : (
                <s.CustomTableCellText>{row[columnKey]}</s.CustomTableCellText>
              );
            },
            tooltipText: (row) => {
              const tip = getStoppedGPUFlavorTip(
                row as TableBillingInstancesFlavor,
                String(currency)
              );
              return tip;
            }
          }}
        />
      )
    },
    {
      title: "Volumes",
      enabled: true,
      content: (
        <TableCollapsible<TableBillingVolumesRecord>
          key={"volumesBilling"}
          rows={volumesTableBillingData || []}
          collapsibleDataKey="itemFlavors"
          isLoading={isProjectBillingVolumesDataLoading}
          isSearchEnabled={true}
          totalRow={volumesTableBillingDataTotal || undefined}
          toolbarItems={toolbarItems}
          columns={[...tableVolumesColumns]}
          collapsibleDataColumns={[...collapsibleVolumesColumns]}
          itemWithIcon={{
            withText: false,
            column: "status",
            getIcon: (value) => {
              if (value === "available")
                return <SuccessIcon color="success" fontSize="small" />;
              if (value === "deleted")
                return <FailedIcon color="error" fontSize="small" />;
              return <></>; // or default icon
            },
            tooltipText: (row) => {
              if (row.deleted === "true") return `deleted volume`;
              if (row.deleted === "false") return "available volume";
              return "";
            }
          }}
        />
      )
    },
    {
      title: "Databases",
      enabled: showDatabases,
      content: (
        <TableCollapsible<TableBillingDatabasesRecord>
          key={"databasesBilling"}
          rows={databasesTableBillingData || []}
          collapsibleDataKey="itemFlavors"
          isLoading={isProjectBillingDatabasesDataLoading}
          isSearchEnabled={true}
          totalRow={databasesTableBillingDataTotal || undefined}
          toolbarItems={toolbarItems}
          columns={[...tableDatabasesColumns]}
          collapsibleDataColumns={[...collapsibleDatabasesColumns]}
          itemWithIcon={{
            withText: false,
            column: "status",
            getIcon: (value) => {
              if (value === "available")
                return <SuccessIcon color="success" fontSize="small" />;
              if (value === "deleted")
                return <FailedIcon color="error" fontSize="small" />;
              return <></>; // or default icon
            },
            tooltipText: (row) => {
              if (row.deleted === "true") return `deleted database`;
              if (row.deleted === "false") return "available database";
              return "";
            }
          }}
        />
      )
    },
    {
      title: "Object Storage",
      enabled: true,
      content: (
        <TableCollapsible<TableBillingS3Record>
          key={"s3Billing"}
          rows={s3TableBillingData || []}
          // collapsibleDataKey="itemFlavors"
          // collapsibleDataColumns={[...collapsibleS3Columns]}
          isLoading={isProjectBillingS3DataLoading}
          isSearchEnabled={true}
          totalRow={s3TableBillingDataTotal || undefined}
          toolbarItems={toolbarItems}
          columns={[...tableS3Columns]}
          itemWithIcon={{
            withText: false,
            column: "status",
            getIcon: (value) => {
              if (value === "available")
                return <SuccessIcon color="success" fontSize="small" />;
              if (value === "deleted")
                return <FailedIcon color="error" fontSize="small" />;
              return <></>; // or default icon
            },
            tooltipText: (row) => {
              if (row.deleted === "true") return `deleted object`;
              if (row.deleted === "false") return "available object";
              return "";
            }
          }}
        />
      )
    },
    {
      title: "Backups",
      enabled: showBackups,
      content: (
        <>
          <TableCollapsible<TableBillingBackupsLicensesRecord>
            key={"BackupsLicensesBilling"}
            tableTitle={"Issued licenses for protected VMs"}
            rows={backupsLicensesTableBillingData || []}
            collapsibleDataKey="itemFlavors"
            isLoading={isProjectBillingBackupsLicensesDataLoading}
            isSearchEnabled={true}
            totalRow={backupsLicensesTableBillingDataTotal || undefined}
            toolbarItems={toolbarItems}
            columns={[...tableBackupsLicensesColumns]}
            collapsibleDataColumns={[...collapsibleBackupsLicensesColumns]}
            collapsibleItemWithIcon={{
              withText: false,
              column: "status",
              getIcon: (row) => {
                if (row["status"] === "available")
                  return <SuccessIcon color="success" fontSize="small" />;
                if (row["status"] === "deleted")
                  return <FailedIcon color="error" fontSize="small" />;
                return <></>;
              },
              tooltipText: (row) => {
                if (row.status === "deleted")
                  return `deleted at ${String(row.deleted)}`;
                if (row.status === "available") return "available backup";
                return "";
              }
            }}
          />
          <TableCollapsible<TableBillingBackupsStorageRecord>
            key={"BackupsStorageBilling"}
            tableTitle={"Used storage"}
            rows={backupsStorageTableBillingData || []}
            collapsibleDataKey="itemFlavors"
            isLoading={isProjectBillingBackupsStorageDataLoading}
            totalRow={backupsStorageTableBillingDataTotal || undefined}
            columns={[...tableBackupsStorageColumns]}
            collapsibleDataColumns={[...collapsibleBackupsStorageColumns]}
            collapsibleItemWithIcon={{
              withText: false,
              column: "status",
              getIcon: (row) => {
                if (row["status"] === "available")
                  return <SuccessIcon color="success" fontSize="small" />;
                if (row["status"] === "deleted")
                  return <FailedIcon color="error" fontSize="small" />;
                return <></>;
              },
              tooltipText: (row) => {
                if (row.status === "deleted")
                  return `deleted at ${String(row.deleted)}`;
                if (row.status === "available") return "available backup";
                return "";
              }
            }}
          />
        </>
      )
    }
  ];

  const visibleTabs = tabConfig.filter((tab) => tab.enabled);
  const tabTitles = visibleTabs.map((tab) => tab.title);
  const tabContent = visibleTabs.map((tab) => tab.content);

  return (
    <>
      <Head title={title} />
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      {!billingOrganization ? (
        // 🔄 Loading
        <s.StubContainer>
          <Loader text="Loading data..." />
        </s.StubContainer>
      ) : [BILLING_ROLES.OWNER, BILLING_ROLES.ADMIN].includes(
          billingOrganization.billing_role
        ) ? (
        // ✅ Has access
        <>
          <s.HeaderBlock
            style={{ visibility: billingProject ? "visible" : "hidden" }}
          >
            <s.Title variant={"h4"} component={"h2"}>
              {billingProject?.name}: {title}
            </s.Title>
            <s.SummaryRow>
              <s.DetailsTitle>ID: </s.DetailsTitle>
              <s.DetailsInfoColored>{billingProject?.id}</s.DetailsInfoColored>
            </s.SummaryRow>
            <s.SummaryRow>
              <s.DetailsTitle>Region: </s.DetailsTitle>
              <s.Tag label={billingProject?.region} />
            </s.SummaryRow>
          </s.HeaderBlock>
          <s.CostsContainer>
            <s.TabsContainer>
              <Tabs value={activeTabIndex} onChange={handleChangeTab}>
                {tabTitles.map((tab) => (
                  <Tab key={tab} label={tab} />
                ))}
              </Tabs>
            </s.TabsContainer>

            <s.Description>
              ℹ️ This billing data is only for the initial reference. The costs
              will be provided in the invoice.
            </s.Description>
            <s.Description2>
              ℹ️ Billing data is available for previous 120 days
            </s.Description2>

            {billingProject && budgetThreshold && (
              <LinearProgressWithLabelBig
                value={
                  currency === "eur"
                    ? Number(summaryTableBillingDataTotal["cost-eur"])
                    : Number(summaryTableBillingDataTotal["cost-chf"])
                }
                maxValue={
                  currency === "eur"
                    ? Number(budgetThreshold["eur"])
                    : Number(budgetThreshold["chf"])
                }
                unit={currency}
                title="Budget Tracker"
                titleWithCaption
              />
            )}

            {tabContent[activeTabIndex]}
          </s.CostsContainer>
        </>
      ) : (
        // 🔒 No permissions
        <s.StubContainer>
          <s.Description style={{ textAlign: "center" }}>
            🔒 You don’t have permission to view billing for this organization
            and its projects.
          </s.Description>
          <s.Description style={{ textAlign: "center" }}>
            Please contact the organization owner to request access.
          </s.Description>
        </s.StubContainer>
      )}

      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
      />
    </>
  );
};
